import startCase from 'lodash.startcase'

import { CreateAccountMutationVariables } from 'src/config/generated/graphql'
import {
  BusinessSize,
  BusinessType,
  FocusAreaType,
  GovernmentType,
  OrganisationType,
  SchoolTertiaryType,
  SectorType,
  DepartmentType
} from 'src/types/account'
import { enumToArray } from 'src/utils/common'
import { DEFAULT_SELECT_OPTION } from 'src/utils/form'

import { AccountFormValues, PrimaryGoalType, ProfileVisibility } from './types'

export const primaryGoalLabelMap: Record<PrimaryGoalType, string> = {
  [PrimaryGoalType.MitigateEmissions]: 'Mitigate our carbon footprint',
  [PrimaryGoalType.ShowcaseImpact]: 'Share and demonstrate our impact',
  [PrimaryGoalType.EngageStaff]: 'Engage our customers and staff',
  [PrimaryGoalType.HelpEnvironment]: 'Restore native forests',
  [PrimaryGoalType.SendECard]: 'Send a native tree e-card as a gift',
  [PrimaryGoalType.FundInMemory]:
    'Fund native trees in memory of someone/a loved one',
  [PrimaryGoalType.MitigateCarbon]: 'Help mitigate my carbon footprint',
  [PrimaryGoalType.DoRightForEnvironment]:
    'Do the right thing for the environment',
  [PrimaryGoalType.Other]: 'Other'
}

const personalGoalTypes = [
  PrimaryGoalType.SendECard,
  PrimaryGoalType.FundInMemory,
  PrimaryGoalType.MitigateCarbon,
  PrimaryGoalType.DoRightForEnvironment,
  PrimaryGoalType.Other
]

export const personalPrimaryGoalOptions = [
  DEFAULT_SELECT_OPTION,
  ...personalGoalTypes.map(value => ({
    label: primaryGoalLabelMap[value],
    value
  }))
]

export const businessTypeOptions = [
  DEFAULT_SELECT_OPTION,
  ...enumToArray(BusinessType)
    .filter(o => o !== BusinessType[BusinessType.Unknown])
    .map(key => ({
      label: startCase(key),
      value: Object(BusinessType)[key]
    }))
]

export const sectorTypeOptions = [
  DEFAULT_SELECT_OPTION,
  ...enumToArray(SectorType)
    .filter(o => o !== SectorType[SectorType.Unknown])
    .map(key => ({
      label: startCase(key),
      value: Object(SectorType)[key]
    }))
]

export const schoolTypeOptions = [
  DEFAULT_SELECT_OPTION,
  ...enumToArray(SchoolTertiaryType)
    .filter(o => o !== SchoolTertiaryType[SchoolTertiaryType.Unknown])
    .map(key => ({
      label: startCase(key),
      value: Object(SchoolTertiaryType)[key]
    }))
]

export const organisationTypeOptions = [
  DEFAULT_SELECT_OPTION,
  ...enumToArray(OrganisationType)
    .filter(o => o !== OrganisationType[OrganisationType.Unknown])
    .map(key => ({
      label: startCase(key),
      value: Object(OrganisationType)[key]
    }))
]

export const focusAreaTypeOptions = [
  DEFAULT_SELECT_OPTION,
  ...enumToArray(FocusAreaType)
    .filter(o => o !== FocusAreaType[FocusAreaType.Unknown])
    .map(key => ({
      label: startCase(key),
      value: Object(FocusAreaType)[key]
    }))
]

export const governmentOrganisationTypeOptions = [
  DEFAULT_SELECT_OPTION,
  ...enumToArray(GovernmentType)
    .filter(o => o !== GovernmentType[GovernmentType.Unknown])
    .map(key => ({
      label: startCase(key),
      value: Object(GovernmentType)[key]
    }))
]

export const businessSizeMap: { [key in BusinessSize]: string } = {
  [BusinessSize.Micro]: '1-10 employees',
  [BusinessSize.Small]: '11-50 employees',
  [BusinessSize.Medium]: '51-200 employees',
  [BusinessSize.Large]: '201-500 employees',
  [BusinessSize.XLarge]: '501-1,000 employees',
  [BusinessSize.Enterprise]: '1,001-5,000 employees',
  [BusinessSize.LargeEnterprise]: '5,001-10,000 employees',
  [BusinessSize.XLargeEnterprise]: '10,001+ employees'
}

export const businessSizeOptions = [
  DEFAULT_SELECT_OPTION,
  ...enumToArray(BusinessSize).map(key => ({
    label: businessSizeMap[key as BusinessSize],
    value: key
  }))
]

export const departmentOptions = [
  DEFAULT_SELECT_OPTION,
  ...enumToArray(DepartmentType).map(key => ({
    label: startCase(key),
    value: Object(DepartmentType)[key]
  }))
]

export const primaryGoalOptions = [
  DEFAULT_SELECT_OPTION,
  ...enumToArray(PrimaryGoalType).map(key => ({
    label: startCase(key),
    value: Object(PrimaryGoalType)[key]
  }))
]

export const transformFormData = (values: AccountFormValues) => {
  const {
    AccountType: accountTypeFormValue,
    FirstName,
    LastName,
    DisplayName,
    Phone,
    Email,
    PrimaryGoal,
    PrimaryGoalNote,
    Department,
    JobTitle
  } = values

  const transformedData: CreateAccountMutationVariables = {
    input: {
      AccountActionType: values.AccountActionType!,
      BusinessType: values?.BusinessType,
      FocusArea: values?.FocusArea,
      GovType: values?.GovType,
      OrgType: values?.OrgType,
      SchoolType: values?.SchoolType,
      Sector: values?.Sector,
      Type: accountTypeFormValue,
      Name: DisplayName,
      Email,
      Phone,
      FirstName,
      LastName,
      FunderHidden: values.FunderHidden === ProfileVisibility.Private,
      PlanterHidden: values.PlanterHidden === ProfileVisibility.Private,
      PlanterName: values.PlanterName || values.DisplayName,
      FunderName: values.FunderName || values.DisplayName,
      BusinessSize: values.BusinessSize,
      PrimaryGoal,
      PrimaryGoalNote,
      Department,
      User: {
        FirstName,
        LastName,
        JobTitle
      }
    }
  }
  return transformedData
}
